import { cn } from '../../lib/utils';

/**
 * La app mostrada en una notebook y en un teléfono, uno encima del otro.
 *
 * Las dos pantallas son JSX, no capturas. Las razones, en orden:
 *
 *   1. No existe ninguna captura de la app en teléfono. La única que hay es
 *      /dashboard.png, que es de escritorio (1917×943) y ya se usa en la
 *      landing dentro de PanelEnPerspectiva.
 *   2. A este tamaño una captura de 1917px no se lee: queda una mancha gris.
 *      Una interfaz dibujada para el tamaño en el que se va a ver sí se lee.
 *   3. Una captura se congela. Esto usa los mismos tokens que el resto
 *      (`bg-primary`, `text-primary`), así que si cambia el color de la marca
 *      las dos pantallitas acompañan solas.
 *
 * Los marcos también son CSS. Nada de PNG de dispositivos: son pesados, y el
 * borde redondeado de un marco ajeno nunca coincide con el de la pantalla que
 * le metés adentro.
 *
 * Sin animación de entrada, a propósito: ver el comentario de Aparecer.jsx.
 */

// Datos de muestra. Son inventados y sólo viven acá: esto es una ilustración,
// no una vista conectada a la API.
//
// Son ocho y no cinco porque la pantalla de la notebook es 16/10: con cinco
// filas de alto natural quedaban dos tercios de pantalla en blanco y no parecía
// una agenda, parecía una agenda vacía. Van con `flex-1` (abajo), así que se
// reparten el alto que haya: se estiran en escritorio y se comprimen en
// teléfono, donde la misma maqueta entra en un tercio del ancho.
const TURNOS_DE_MUESTRA = [
  { hora: '09:00', cliente: 'Camila Ruiz', servicio: 'Corte', estado: 'confirmado' },
  { hora: '10:30', cliente: 'Martín Sosa', servicio: 'Color', estado: 'confirmado' },
  { hora: '11:45', cliente: 'Julieta Vega', servicio: 'Brushing', estado: 'confirmado' },
  { hora: '12:30', cliente: 'Lucía Ferrer', servicio: 'Corte', estado: 'pendiente' },
  { hora: '14:00', cliente: 'Diego Paz', servicio: 'Corte + barba', estado: 'confirmado' },
  { hora: '15:30', cliente: 'Ana Molina', servicio: 'Color', estado: 'pendiente' },
  { hora: '16:45', cliente: 'Tomás Iglesias', servicio: 'Corte', estado: 'confirmado' },
  { hora: '18:00', cliente: 'Sofía Aguirre', servicio: 'Brushing', estado: 'confirmado' },
];

const SECCIONES = ['Turnos', 'Clientes', 'Servicios', 'Personal', 'Horarios'];

/** La agenda del día, como se ve en el panel. Va adentro de la notebook. */
function PanelDelDia() {
  return (
    <div className="flex h-full w-full bg-neutral-50 text-neutral-900">
      {/* Barra lateral */}
      <div className="flex w-[24%] shrink-0 flex-col gap-2 border-r border-neutral-200 bg-white p-2">
        <div className="flex items-center gap-1">
          <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          <span className="text-[7px] font-bold tracking-tight">Aturno</span>
        </div>
        <div className="space-y-[3px]">
          {SECCIONES.map((seccion, i) => (
            <div
              key={seccion}
              className={cn(
                'flex items-center gap-1 rounded px-1 py-[3px] text-[6px] leading-none',
                i === 0 ? 'bg-primary/10 font-semibold text-primary' : 'text-neutral-400'
              )}
            >
              <span
                className={cn(
                  'h-[3px] w-[3px] shrink-0 rounded-full',
                  i === 0 ? 'bg-primary' : 'bg-neutral-300'
                )}
              />
              {seccion}
            </div>
          ))}
        </div>
      </div>

      {/* Agenda */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-2">
        <div className="flex items-baseline justify-between">
          <div>
            <p className="text-[8px] font-bold leading-none">Turnos de hoy</p>
            <p className="mt-[3px] text-[6px] leading-none text-neutral-400">Miércoles 12</p>
          </div>
          <span className="rounded-full bg-primary px-1.5 py-[2px] text-[6px] font-semibold leading-none text-primary-foreground">
            8 turnos
          </span>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-[3px]">
          {TURNOS_DE_MUESTRA.map((turno) => (
            <div
              key={turno.hora}
              className="flex min-h-0 flex-1 items-center gap-1.5 overflow-hidden rounded border border-neutral-200 bg-white px-1.5"
            >
              <span className="w-[22px] shrink-0 text-[6px] font-semibold leading-none tabular-nums text-neutral-900">
                {turno.hora}
              </span>
              <span className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[5px] font-bold leading-none text-primary">
                {turno.cliente.charAt(0)}
              </span>
              <span className="min-w-0 flex-1 truncate text-[6px] leading-none text-neutral-700">
                {turno.cliente}
              </span>
              <span className="min-w-0 flex-1 truncate text-[6px] leading-none text-neutral-400">
                {turno.servicio}
              </span>
              <span
                className={cn(
                  'shrink-0 rounded-full px-1 py-[2px] text-[5px] font-semibold leading-none',
                  turno.estado === 'confirmado'
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'bg-amber-50 text-amber-600'
                )}
              >
                {turno.estado === 'confirmado' ? 'Confirmado' : 'Pendiente'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** La página pública del negocio, como la ve un cliente. Va adentro del teléfono. */
function ReservaEnElTelefono() {
  return (
    <div className="flex h-full w-full flex-col bg-white text-neutral-900">
      {/* Encabezado del negocio */}
      <div className="shrink-0 bg-primary px-2 pb-2 pt-5 text-primary-foreground">
        <p className="text-[8px] font-bold leading-none">Estudio Norte</p>
        <p className="mt-[3px] text-[6px] leading-none opacity-80">Peluquería · Palermo</p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 p-2">
        <div>
          <p className="text-[6px] font-semibold leading-none text-neutral-400">1. Servicio</p>
          <div className="mt-1 space-y-[3px]">
            <div className="flex items-center justify-between rounded border border-primary bg-primary/5 px-1.5 py-[5px]">
              <span className="text-[6px] font-semibold leading-none">Corte</span>
              <span className="text-[6px] font-semibold leading-none text-primary">$8.000</span>
            </div>
            <div className="flex items-center justify-between rounded border border-neutral-200 px-1.5 py-[5px]">
              <span className="text-[6px] leading-none text-neutral-600">Color</span>
              <span className="text-[6px] leading-none text-neutral-400">$15.000</span>
            </div>
          </div>
        </div>

        <div>
          <p className="text-[6px] font-semibold leading-none text-neutral-400">2. Horario</p>
          <div className="mt-1 grid grid-cols-3 gap-[3px]">
            {['10:00', '11:30', '14:00', '15:30', '17:00', '18:30'].map((hora, i) => (
              <span
                key={hora}
                className={cn(
                  'rounded border px-[2px] py-[4px] text-center text-[6px] font-medium leading-none tabular-nums',
                  i === 2
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-neutral-200 text-neutral-600'
                )}
              >
                {hora}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-auto rounded-full bg-primary py-[6px] text-center text-[7px] font-bold leading-none text-primary-foreground">
          Reservar turno
        </div>
      </div>
    </div>
  );
}

export default function EquiposEnUso({ className }) {
  return (
    // `pb-*` y `pl-*` dejan lugar para el teléfono, que está posicionado por
    // fuera del borde de la notebook. Sin eso, el contenedor recorta.
    <div className={cn('relative w-full max-w-[560px] pb-8 pl-6 sm:pb-10 sm:pl-10', className)}>
      {/* Notebook */}
      <div aria-hidden="true">
        <div className="rounded-t-[10px] bg-neutral-900 p-[6px] shadow-2xl ring-1 ring-black/20 sm:rounded-t-xl sm:p-2">
          <div className="aspect-[16/10] overflow-hidden rounded-[4px] bg-white sm:rounded-md">
            <PanelDelDia />
          </div>
        </div>
        {/* Base. Más ancha que la pantalla, como una notebook abierta de frente. */}
        <div className="relative left-1/2 h-2.5 w-[108%] -translate-x-1/2 rounded-b-lg bg-gradient-to-b from-neutral-700 to-neutral-900 shadow-lg sm:h-3">
          <span className="absolute left-1/2 top-0 h-1 w-12 -translate-x-1/2 rounded-b-full bg-neutral-900/70 sm:w-16" />
        </div>
      </div>

      {/* Teléfono, apoyado sobre la esquina de la notebook */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 w-[86px] sm:w-[112px]"
      >
        <div className="rounded-[16px] bg-neutral-900 p-[4px] shadow-2xl ring-1 ring-black/20 sm:rounded-[22px] sm:p-[5px]">
          <div className="relative aspect-[9/18] overflow-hidden rounded-[13px] bg-white sm:rounded-[18px]">
            {/* Isla dinámica */}
            <span className="absolute left-1/2 top-[5px] z-10 h-[7px] w-[26px] -translate-x-1/2 rounded-full bg-neutral-900 sm:h-2 sm:w-8" />
            <ReservaEnElTelefono />
          </div>
        </div>
      </div>

      {/* Los dos equipos son decorativos (aria-hidden): lo que dicen ya está
          escrito al lado, en el título y la bajada del cierre. Esto es para
          quien lee la pantalla sin verla. */}
      <p className="sr-only">
        Aturno funciona en la computadora y en el teléfono: el panel con los
        turnos del día, y la página donde tus clientes reservan solos.
      </p>
    </div>
  );
}
